import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Breadcrumb, Button, Layout } from "antd";
import {
  PrinterOutlined,
  MailOutlined,
  WhatsAppOutlined,
  ArrowLeftOutlined,
  CheckOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import Loader from "../../../Home/Loader/Loader";
import { getGateEntriesById, updateGateEntry } from "../../../redux/actions/API/gateEntry";
import { GateEntry } from "./DownloadGateEntryPdf";
import "../previewDocument/PreviewDocument.css";
import { getPartialUser } from "../../../redux/actions/API/users";
import "./PreviewGateEntry.css";

const statusMap: Record<number, { label: string; color: string }> = {
  0: { label: "Pending", color: "#faad14" },
  1: { label: "Exited", color: "#52c41a" },
  2: { label: "Checked Out", color: "#1677ff" },
  3: { label: "Rejected", color: "#ff4d4f" },
};

interface IPreviewGateEntryProps {
  getGateEntriesById: Function;
  companyId: any;
  entryData: GateEntry;
  loading: boolean;
  userData: any;
  getPartialUser: Function;
  updateGateEntryStatus: Function;
  updateGateEntryState: any;
}

const PreviewGateEntry: React.FC<IPreviewGateEntryProps> = ({
  getGateEntriesById,
  companyId,
  entryData,
  loading: entryLoading,
  userData,
  getPartialUser,
  updateGateEntryStatus,
  updateGateEntryState
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const documentRef = useRef<HTMLDivElement>(null);
  const prevUpdateStateRef = useRef<any>(null);

  const isFromReports = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("fromReports") === "true";
  };

  const breadcrumbItems = [
    { title: "Dashboard" },
    { title: "Gate Entries" },
    { title: "Preview Gate Entry" },
  ];

  useEffect(() => {
    const url = window.location.href;
    const params = new URLSearchParams(url.split("?")[1]);
    const documentNumber = params.get("documentNumber");

    if (documentNumber) {
      getGateEntriesById({ id: documentNumber, companyId: Number(companyId) });
       getPartialUser({ companyId: Number(companyId) });
      setLoading(true);
    }
  }, [getGateEntriesById, companyId]);

    const handleCloseEntry = () => {
      const url = window.location.href;
      const params = new URLSearchParams(url.split("?")[1]);
      const documentNumber = params.get("documentNumber");
      if (documentNumber) {
        setLoading(true);
        updateGateEntryStatus({
          id: documentNumber,
        });
      }
    };

  useEffect(() => {
    if (prevUpdateStateRef.current?.loading && !updateGateEntryState?.loading) {
      if (updateGateEntryState?.error) {
        console.error("Error closing gate entry:", updateGateEntryState.error);
        setLoading(false);
      } else {
        const url = window.location.href;
        const params = new URLSearchParams(url.split("?")[1]);
        const documentNumber = params.get("documentNumber");
        if (documentNumber) {
          getGateEntriesById({ id: documentNumber, companyId: Number(companyId) });
        }
      }
    }
    prevUpdateStateRef.current = updateGateEntryState;
  }, [updateGateEntryState]);

  useEffect(() => {
    if (!entryLoading) {
      setLoading(false);
    }
  }, [entryLoading]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return (
      date.toLocaleDateString("en-GB") +
      " " +
      date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    );
  };

  const getStatusInfo = (status: number) => {
    return statusMap[status] || { label: "Unknown", color: "#999" };
  };

  const handlePrint = () => {
    // TODO: implement print logic
    window.print();
  };



  return (
    <>
      <Loader loading={loading || entryLoading} />
      <Layout style={{ padding: "15px" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <div>
            <Button
              type="link"
              onClick={handlePrint}
            >
              <PrinterOutlined /> 
              Print
            </Button>
          </div>
        </div>

        <div className="flexBox mt-10 documentMobileRow">
          <div className="divDocuments Mt-10">
            <Button
              type="link"
              onClick={() => (isFromReports() ? navigate(-1) : navigate("/gate_entries"))}
            >
              <p className="textDocument">
                <ArrowLeftOutlined /> {entryData?.documentNumber}
              </p>
            </Button>
          </div>
          <div
            className="documentButtonRow"
            style={{ display: "flex", justifyContent: "space-between" ,}}
          >
            { entryData && entryData.status === 0 && <Button
              type="link"
              onClick={handleCloseEntry}
              
            >
              <CheckCircleOutlined/>
              Close Entry
            </Button>}
          </div>
        </div>

        {/* Gate Entry Preview Content */}
        {entryData?.documentNumber ? (
          <div
            ref={documentRef}
            className="documentContainer"
            id="documentContainer"
          >
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "20px",
                }}
              >
                 {userData?.logoUrl && (
                  <img
                    src={userData?.logoUrl}
                    alt="EaseMargin"
                    style={{ maxHeight: "70px", marginBottom: "8px" }}
                  />
                )}
                <div style={{ textAlign: "right" }}>
                  <h3 style={{ margin: 0 }}>Gate Entry Pass</h3>
                  <div className="title">
                    Entry No: {entryData.documentNumber}
                  </div>
                  <div
                    style={{
                      marginTop: "6px",
                      padding: "2px 12px",
                      borderRadius: "4px",
                      display: "inline-block",
                      fontWeight: 500,
                      color: getStatusInfo(entryData.status).color,
                      border: `1px solid ${getStatusInfo(entryData.status).color}`,
                    }}
                  >
                    {getStatusInfo(entryData.status).label}
                  </div>
                </div>
              </div>

              <hr />

              {/* Visitor Details & Photo */}
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  gap: "20px",
                  marginTop: "15px",
                }}
              >
                {/* Visitor Info */}
                <div
                  style={{
                    flex: 2,
                    borderRight: "1px solid #ccc",
                    paddingRight: "20px",
                  }}
                >
                  <h6 style={{ marginBottom: "10px" }}>Visitor Details</h6>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                    }}
                  >
                    <tbody>
                      <tr>
                        <td
                          style={{
                            padding: "6px 8px",
                            fontWeight: 500,
                            width: "40%",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          Visitor Name
                        </td>
                        <td
                          style={{
                            padding: "6px 8px",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          {entryData.visitorName}
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            padding: "6px 8px",
                            fontWeight: 500,
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          Contact Number
                        </td>
                        <td
                          style={{
                            padding: "6px 8px",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          +91-{entryData.visitorContact}
                        </td>
                      </tr>
                      {entryData.visitorEmail && (
                        <tr>
                          <td
                            style={{
                              padding: "6px 8px",
                              fontWeight: 500,
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            Email
                          </td>
                          <td
                            style={{
                              padding: "6px 8px",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            {entryData.visitorEmail}
                          </td>
                        </tr>
                      )}
                      {entryData.visitorCompany && (
                        <tr>
                          <td
                            style={{
                              padding: "6px 8px",
                              fontWeight: 500,
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            Visitor Company
                          </td>
                          <td
                            style={{
                              padding: "6px 8px",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            {entryData.visitorCompany}
                          </td>
                        </tr>
                      )}
                      {entryData.idProofType && (
                        <tr>
                          <td
                            style={{
                              padding: "6px 8px",
                              fontWeight: 500,
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            ID Proof Type
                          </td>
                          <td
                            style={{
                              padding: "6px 8px",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            {entryData.idProofType}
                          </td>
                        </tr>
                      )}
                      {entryData.idProofNumber && (
                        <tr>
                          <td
                            style={{
                              padding: "6px 8px",
                              fontWeight: 500,
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            ID Proof Number
                          </td>
                          <td
                            style={{
                              padding: "6px 8px",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            {entryData.idProofNumber}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Visitor Photo */}
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                  }}
                >
                  {entryData.visitorImageUrl ? (
                    <img
                      src={entryData.visitorImageUrl}
                      alt="Visitor"
                      style={{
                        width: "150px",
                        height: "180px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "150px",
                        height: "180px",
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#999",
                        fontSize: "12px",
                      }}
                    >
                      No Photo
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: "6px",
                      fontSize: "12px",
                      color: "#666",
                    }}
                  >
                    Visitor Photo
                  </div>
                </div>
              </div>

              <hr style={{ margin: "15px 0" }} />

              {/* Visit Details */}
              <div style={{ marginTop: "10px" }}>
                <h6 style={{ marginBottom: "10px" }}>Visit Details</h6>
                <table
                  className="documentTable"
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    border: "1px solid silver",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          border: "1px solid silver",
                          padding: "8px",
                          width: "25%",
                        }}
                      >
                        Purpose of Visit
                      </th>
                      <th
                        style={{
                          border: "1px solid silver",
                          padding: "8px",
                          width: "25%",
                        }}
                      >
                        Person to Meet
                      </th>
                      <th
                        style={{
                          border: "1px solid silver",
                          padding: "8px",
                          width: "25%",
                        }}
                      >
                        Entry Time
                      </th>
                      {entryData.updatedAt &&
                        (entryData.status === 1 || entryData.status === 2) && (
                          <th
                            style={{
                              border: "1px solid silver",
                              padding: "8px",
                              width: "25%",
                            }}
                          >
                            Exit Time
                          </th>
                        )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td
                        style={{
                          border: "1px solid silver",
                          padding: "8px",
                        }}
                      >
                        {entryData.purposeOfVisit}
                      </td>
                      <td
                        style={{
                          border: "1px solid silver",
                          padding: "8px",
                        }}
                      >
                        {entryData.personToMeet}
                      </td>
                      <td
                        style={{
                          border: "1px solid silver",
                          padding: "8px",
                        }}
                      >
                        {formatDate(entryData.createdAt)}
                      </td>
                      {entryData.updatedAt &&
                        (entryData.status === 1 || entryData.status === 2) && (
                          <td
                            style={{
                              border: "1px solid silver",
                              padding: "8px",
                            }}
                          >
                            {formatDate(entryData.updatedAt)}
                          </td>
                        )}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Vehicle Details */}
              {(entryData.vehicleNumber || entryData.vehicleType) && (
                <div style={{ marginTop: "20px" }}>
                  <h6 style={{ marginBottom: "10px" }}>Vehicle Details</h6>
                  <table
                    className="documentTable"
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      border: "1px solid silver",
                    }}
                  >
                    <thead>
                      <tr>
                        {entryData.vehicleType && (
                          <th
                            style={{
                              border: "1px solid silver",
                              padding: "8px",
                            }}
                          >
                            Vehicle Type
                          </th>
                        )}
                        {entryData.vehicleNumber && (
                          <th
                            style={{
                              border: "1px solid silver",
                              padding: "8px",
                            }}
                          >
                            Vehicle Number
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {entryData.vehicleType && (
                          <td
                            style={{
                              border: "1px solid silver",
                              padding: "8px",
                            }}
                          >
                            {entryData.vehicleType}
                          </td>
                        )}
                        {entryData.vehicleNumber && (
                          <td
                            style={{
                              border: "1px solid silver",
                              padding: "8px",
                            }}
                          >
                            {entryData.vehicleNumber}
                          </td>
                        )}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Authorized By */}
              {entryData.user && (
                <>
                  <hr style={{ margin: "15px 0" }} />
                  <div style={{ marginTop: "10px" }}>
                    <h6 style={{ marginBottom: "10px" }}>Authorized By</h6>
                    <div>
                      <span className="title">Name: </span>
                      {entryData.user.name}
                    </div>
                    {entryData.user.email && (
                      <div>
                        <span className="title">Email: </span>
                        {entryData.user.email}
                      </div>
                    )}
                    {entryData.user.contactNo && (
                      <div>
                        <span className="title">Contact: </span>
                        +91-{entryData.user.contactNo}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Comments */}
              {entryData.comments && (
                <>
                  <hr style={{ margin: "15px 0" }} />
                  <div style={{ marginTop: "10px" }}>
                    <h6 style={{ marginBottom: "10px" }}>Comments</h6>
                    <div>{entryData.comments}</div>
                  </div>
                </>
              )}

              {/* Signatures */}
              <hr style={{ margin: "15px 0" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  marginTop: "20px",
                }}
              >
                {/* Visitor Signature */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: "150px",
                      height: "60px",
                      borderBottom: "1px solid #000",
                    }}
                  />
                  <p>Visitor Signature</p>
                </div>

                {/* Security Signature */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {entryData.securitySignatureUrl ? (
                    <img
                      src={entryData.securitySignatureUrl}
                      alt="Security Signature"
                      style={{
                        objectFit: "contain",
                        maxWidth: "120px",
                        maxHeight: "80px",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "150px",
                        height: "60px",
                        borderBottom: "1px solid #000",
                      }}
                    />
                  )}
                  <p>Security Signature</p>
                </div>
              </div>
            </div>
        ) : (
          <div className="noData">
            <p>No gate entry data found.</p>
          </div>
        )}
      </Layout>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  companyId: state.api.login?.data?.companyId,
  entryData: state.api.getGateEntryByID?.data?.data,
  loading: state.api.getGateEntryByID?.loading,
  userData: state.api.getPartialUser?.data,
  updateGateEntryState: state.api.updateGateEntry,
});

const mapDispatchToProps = (dispatch: any) => ({
  getGateEntriesById: (payload: any) => dispatch(getGateEntriesById(payload)),
    getPartialUser: (payload: any) => dispatch(getPartialUser(payload)),
    updateGateEntryStatus: (payload: any) => dispatch(updateGateEntry(payload)),
  
});

export default connect(mapStateToProps, mapDispatchToProps)(PreviewGateEntry);